import {
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MicaChatCajaChat } from '@modules/mica-chat/components/mica-chat-caja-chat/mica-chat-caja-chat';
import { MicaChat_ChatsEntity } from '@modules/mica-chat/entities/chats.mica-chat';
import { MicaChatCajaMensajes } from '@modules/mica-chat/components/mica-chat-caja-mensajes/mica-chat-caja-mensajes';
import { MicaChat_MensajesEntity } from '@modules/mica-chat/entities/mensajes.mica-chat';
import { MicaChatQrConexion } from '@modules/mica-chat/components/mica-chat-qr-conexion/mica-chat-qr-conexion';
import { MicaChatApiService } from '@core/services/api/mica-chat-api.service';
import { EvolutionApiService } from '@core/services/api/evolution-api.service';
import { MicaChatService } from '@shared/services/mica-chat.service';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { SucursalService } from '@shared/services/sucursal.service';
import { AppService } from '@shared/services/app.service';
import { MicaChatListenerService } from '@shared/services/websocket/mica-chat-listener.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IMicaChatTelefonoData } from '@core/interfaces/entidades/telefono.entidad';
import { DeviceService } from '@shared/services/device.service';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-micachat-chats',
  imports: [MicaChatCajaChat, MicaChatCajaMensajes, MicaChatQrConexion],
  templateUrl: './chats.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './chats.css',
})
export class Chats implements OnInit, OnDestroy {
  constructor() {
    effect(
      () => {
        if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
          this.micaChatListenerService.start(this.sucursalService.activeSucursal()!.id);
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const estado = this.micaChatService.estadoConexion();
      if (estado?.estado === 'conectado') {
        const telefono = this.micaChatService.getTelefonoSeleccionado();
        if (telefono) this.cargarChats(telefono);
      }
    });
  }
  private readonly micaChatService = inject(MicaChatService);
  private readonly metaApi = inject(MicaChatApiService);
  private readonly evoApi = inject(EvolutionApiService);
  private readonly micaChatListenerService = inject(MicaChatListenerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sucursalService = inject(SucursalService);
  private readonly appService = inject(AppService);
  private readonly deviceService = inject(DeviceService);
  private readonly toastService = inject(ToastService);

  chats = signal<MicaChat_ChatsEntity[]>([]);
  mensajes = signal<MicaChat_MensajesEntity[]>([]);
  chatSeleccionado = signal<MicaChat_ChatsEntity | null>(null);
  mostrarListaChatsMovil = signal(true);

  readonly mostrarQrConexion = computed(() => {
    const telefono = this.micaChatService.telefonoSeleccionadoSignal();
    if (!telefono || telefono.proveedor !== 'NO_OFICIAL') return false;
    const estado = this.micaChatService.estadoConexion();
    return !estado || estado.estado !== 'conectado';
  });

  get telefonoId(): number {
    return this.micaChatService.telefonoSeleccionadoSignal()?.id ?? 0;
  }

  onConexionEstablecida(): void {
    const telefono = this.micaChatService.getTelefonoSeleccionado();
    if (telefono) this.cargarChats(telefono);
  }

  onSelectChat(chat: MicaChat_ChatsEntity) {
    const telefono = this.micaChatService.getTelefonoSeleccionado();
    if (!telefono) return;

    const request$ =
      telefono.proveedor === 'NO_OFICIAL'
        ? this.evoApi.cargarMensajes(telefono.id, String(chat.remoteJid || chat.id))
        : this.metaApi.cargarMensajes(chat.id as number);

    request$.subscribe({
      next: (mensajes) => {
        this.micaChatService.setChatSeleccionado(chat);
        this.mensajes.set(mensajes);
        if (chat.mensajes_no_leidos > 0) {
          if (telefono.proveedor !== 'NO_OFICIAL') {
            this.metaApi.marcarChatComoLeido(chat.id as number);
          } else {
            this.evoApi.marcarChatComoLeido(telefono.id, chat.id as string).subscribe();
          }
        }
        if (this.deviceService.isBrowser() && !this.deviceService.isDesktop()) {
          this.mostrarListaChatsMovil.set(false);
        }
      },
      error: (error) => {
        const errorMsg = error?.error?.message;
        this.toastService.show(
          'top-right',
          'error',
          'Error al cargar mensajes',
          errorMsg || 'No se pudieron cargar los mensajes del chat seleccionado.',
        );
      },
    });
  }

  volverAChats() {
    this.micaChatService.setChatSeleccionado(null);
    this.mensajes.set([]);
    this.mostrarListaChatsMovil.set(true);
  }

  ngOnInit(): void {
    this.micaChatListenerService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notification) => {
        this.handleWebSocketEvent(notification);
      });

    this.micaChatService.chatSeleccionado$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((chat) => {
        this.chatSeleccionado.set(chat);
      });

    this.micaChatService.telefonoSeleccionado$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((telefono) => {
        this.cargarChats(telefono);
      });
  }

  private cargarChats(telefono: IMicaChatTelefonoData | null) {
    if (!telefono || telefono.id <= 0) return;
    if (telefono.proveedor === 'NO_OFICIAL') {
      this.evoApi.cargarChats(telefono.id).subscribe({
        next: (chats) => this.chats.set(this.sortChats(chats)),
        error: (error) => {
          console.error('Error al cargar chats Evolution:', error);
          this.toastService.show('top-right', 'error', 'Error', 'No se pudieron cargar los chats.');
        },
      });
      return;
    }
    this.metaApi.cargarChats(telefono.id).subscribe({
      next: (chats) => {
        this.chats.set(this.sortChats(chats));
      },
      error: (error) => {
        const errorMsg = error?.error?.message;
        this.toastService.show(
          'top-right',
          'error',
          'Error al cargar chats',
          errorMsg || 'No se pudieron cargar los chats. Intente nuevamente.',
        );
      },
    });
  }

  private handleWebSocketEvent(notification: IEventNotification): void {
    const handler = this.eventHandlers[notification.event];
    handler?.(notification);
  }

  ngOnDestroy(): void {
    const sucursalId = this.sucursalService.activeSucursal()?.id;
    if (sucursalId) {
      this.micaChatListenerService.stop(sucursalId);
    }
  }

  private updateMensaje(mensajeActualizado: MicaChat_MensajesEntity) {
    this.mensajes.update((mensajes) =>
      mensajes.map((mensaje) =>
        mensaje.uuid === mensajeActualizado.uuid ? mensajeActualizado : mensaje,
      ),
    );
  }

  private addChat(chatNuevo: MicaChat_ChatsEntity & { remoteJidAlt?: string }) {
    const altJid = (chatNuevo as unknown as Record<string, unknown>)['remoteJidAlt'] as
      | string
      | undefined;
    this.chats.update((chats) => {
      const existingById = chats.find((c) => c.id === chatNuevo.id || c.id === altJid);
      if (existingById) return chats;
      const existingByPic =
        chatNuevo.profilePictureUrl && !chatNuevo.esGrupo
          ? chats.find((c) => c.profilePictureUrl === chatNuevo.profilePictureUrl && !c.esGrupo)
          : undefined;
      return this.sortChats(
        existingByPic
          ? chats.map((c) =>
              c.id === existingByPic.id
                ? { ...chatNuevo, id: existingByPic.id, remoteJid: existingByPic.remoteJid }
                : c,
            )
          : [...chats, chatNuevo],
      );
    });
  }

  private sortChats(chats: MicaChat_ChatsEntity[]): MicaChat_ChatsEntity[] {
    return [...chats].sort(
      (a, b) =>
        new Date(b.fecha_ultimo_mensaje ?? 0).getTime() -
        new Date(a.fecha_ultimo_mensaje ?? 0).getTime(),
    );
  }

  private updateChat(chatActualizado: Partial<MicaChat_ChatsEntity> & { remoteJidAlt?: string }) {
    const altJid = (chatActualizado as unknown as Record<string, unknown>)['remoteJidAlt'] as
      | string
      | undefined;
    this.chats.update((chats) => {
      const target = chats.find((chat) => {
        return (
          chat.id === chatActualizado.id ||
          chat.id === altJid ||
          (chat as unknown as Record<string, unknown>)['remoteJidAlt'] === chatActualizado.id ||
          (!chatActualizado.id &&
            chat.nombre_contacto === chatActualizado.nombre_contacto &&
            !chat.esGrupo)
        );
      });
      if (!target) return chats;
      const chatActual = this.micaChatService.getChatSeleccionado();
      const esChatActual =
        chatActual && (chatActual.id === target.id || chatActual.remoteJid === target.remoteJid);
      if (!esChatActual) {
        this.reproducirSonido();
      }
      return this.sortChats(
        chats.map((chat) => (chat === target ? { ...chat, ...chatActualizado } : chat)),
      );
    });
  }

  private reproducirSonido(): void {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      /* audio no disponible */
    }
  }

  private addMensaje(mensajeNuevo: MicaChat_MensajesEntity & { remoteJidAlt?: string }) {
    const chatActual = this.micaChatService.getChatSeleccionado();
    if (!chatActual) return;
    const chatIdActual = String(chatActual.id);
    const chatIdMensaje = String(mensajeNuevo.chat_id);
    const altJid = (mensajeNuevo as unknown as Record<string, unknown>)['remoteJidAlt'] as
      | string
      | undefined;
    const coincide =
      chatIdMensaje === chatIdActual ||
      chatIdMensaje === (chatActual as unknown as Record<string, unknown>)['remoteJidAlt'] ||
      altJid === chatIdActual ||
      chatIdMensaje === chatActual.remoteJid ||
      chatActual.remoteJid === altJid;
    if (!coincide) {
      const matchingChat = this.chats().find(
        (c) =>
          c.id === chatIdMensaje ||
          c.id === altJid ||
          (c as unknown as Record<string, unknown>)['remoteJidAlt'] === chatIdMensaje ||
          c.remoteJid === chatIdMensaje,
      );
      if (
        matchingChat &&
        !chatActual.esGrupo &&
        matchingChat.nombre_contacto === chatActual.nombre_contacto
      ) {
        /* mismo contacto con distinto JID, aceptar */
      } else {
        this.reproducirSonido();
        return;
      }
    }
    this.mensajes.update((mensajes) => {
      if (mensajes.some((m) => m.uuid === mensajeNuevo.uuid)) return mensajes;
      const updated = [...mensajes, mensajeNuevo];
      updated.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
      return updated;
    });
  }

  private readonly eventHandlers: Partial<
    Record<EVENT_GATEWAY, (notification: IEventNotification) => void>
  > = {
    [EVENT_GATEWAY.CONNECT]: () => {
      const telefono = this.micaChatService.getTelefonoSeleccionado();
      this.cargarChats(telefono);
    },
    [EVENT_GATEWAY.MICA_CHAT_CHAT_NUEVO]: (n) => this.addChat(n.data as MicaChat_ChatsEntity),
    [EVENT_GATEWAY.MICA_CHAT_CHAT_ACTUALIZADO]: (n) =>
      this.updateChat(n.data as MicaChat_ChatsEntity),
    [EVENT_GATEWAY.MICA_CHAT_MENSAJE_NUEVO]: (n) =>
      this.addMensaje(n.data as MicaChat_MensajesEntity),
    [EVENT_GATEWAY.MICA_CHAT_MENSAJE_ACTUALIZADO]: (n) =>
      this.updateMensaje(n.data as MicaChat_MensajesEntity),
  };
}
